import * as Sentry from '@sentry/react';
import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import Summary from '@pages/project/summary/Summary';
import ProjectAccessLayout from '@pages/project/_utils/ProjectAccessLayout';
import NotificationComponentsPage from '@pages/notification-components/NotificationComponentsPage';
import Root from './Root';
import ErrorPage from './pages/ErrorPage';
import { loader as adminLoader } from './pages/admin';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import MapPage from './pages/map/MapPage';
import { NavigationPath, ProjectPath } from './utils/getMenuContent';

const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const ProjectsPage = lazy(() => import('./pages/projects/Overview'));
const ProjectGuidancePage = lazy(
    () => import('./pages/project/guidance/ProjectGuidancePage'),
);
const SecurityZonesAssets = lazy(
    () => import('@pages/project/securityZonesAssets/SecurityZonesAssets'),
);

const ProjectSecurityNotificationsPage = lazy(
    () =>
        import(
            '@pages/project/security-notifications/notifications/ProjectSecurityNotificationsPage'
        ),
);
const ProjectVulnerabilitiesPage = lazy(
    () =>
        import(
            '@pages/project/security-notifications/vulnerabilities/ProjectVulnerabilitiesPage'
        ),
);
const MeasuresPage = lazy(() => import('@pages/project/measures'));

const ProjectSystemsAndAssetsPage = lazy(
    () =>
        import(
            '@pages/project/systems-and-assets/systems/ProjectSystemsAndAssetsPage'
        ),
);

const ProjectSystemPage = lazy(
    () => import('@pages/project/systems-and-assets/system/ProjectSystemPage'),
);
const ZoneDetailPage = lazy(
    () =>
        import('@pages/project/securityZonesAssets/zoneDetail/ZoneDetailPage'),
);
const PSSProjectClassificationPage = lazy(
    () => import('@pages/project/pss/PSSProjectClassificationPage'),
);
const ThreatAndRiskAnalysisPage = lazy(
    () => import('@tra/ThreatAndRiskAnalysisPage'),
);
const ThreatAndRiskAnalysisListPage = lazy(
    () =>
        import(
            '@pages/project/threat-and-risk-analysis-list/threat-and-risk-analysis-list-page'
        ),
);
const ProjectComponentsPage = lazy(
    () => import('@pages/project/components/ProjectComponentsPage'),
);

const ProjectSettingsPage = lazy(
    () => import('@pages/project/settings/ProjectSettings'),
);

const CraLayout = lazy(() => import('@pages/project/cra/CraLayout'));
const CraPage = lazy(() => import('@pages/project/cra/CraPage'));
const CraTopicPage = lazy(() => import('@pages/project/cra/CraTopicPage'));

const StandardsListPage = lazy(() => import('./pages/standards/StandardsList'));
const StandardDetailsPage = lazy(
    () => import('./pages/standards/StandardDetails'),
);
const RequirementListPage = lazy(
    () => import('./pages/standards/requirements'),
);

const GlobalSecurityNotificationsPage = lazy(
    () =>
        import(
            './pages/security-notifications/GlobalSecurityNotificationsPage'
        ),
);
const GlobalTasksPage = lazy(() => import('./pages/tasks/GlobalTasksPage'));

const AdminPage = lazy(() => import('./pages/admin/AdminSectionController'));
const AdminUsersPage = lazy(() => import('./pages/admin/Users'));
const AdminStandardsPage = lazy(() => import('./pages/admin/Standards'));
const AdminRequirementsPage = lazy(() => import('./pages/admin/Requirements'));
const AdminMappingsPage = lazy(() => import('./pages/admin/Mappings'));
const AdminPSSIndex = lazy(() => import('./pages/admin/PSS'));
const SupplierAssessments = lazy(
    () => import('./pages/admin/SupplierAssessments'),
);
const AdminProjectGuidancePage = lazy(
    () =>
        import(
            './pages/admin/ProjectGuidanceConfigurator/ProjectGuidanceConfigurator'
        ),
);

const ProfilePage = lazy(() => import('./pages/me'));

const sentryCreateBrowserRouter =
    Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const Routes = [
    {
        path: '/',
        element: <Root />,
        errorElement: <ErrorPage />,
        children: [
            {
                index: true,
                element: <DashboardPage />,
            },
            {
                path: '/map',
                element: <MapPage />,
            },
            {
                path: NavigationPath.Projects,
                element: <ProjectsPage />,
            },
            {
                path: NavigationPath.Tasks,
                element: <GlobalTasksPage />,
            },
            {
                path: `${NavigationPath.Projects}/:pid`,
                element: <ProjectAccessLayout />,
                children: [
                    {
                        index: true,
                        element: <Summary />,
                        exact: true,
                        strict: true,
                    },
                    {
                        path: `${ProjectPath.Guidance.substring(1)}/*`,
                        element: <ProjectGuidancePage />,
                        children: [
                            {
                                path: ':tid',
                                element: <ProjectGuidancePage />,
                            },
                        ],
                    },
                    {
                        path: ProjectPath.Settings.substring(1),
                        element: <ProjectSettingsPage />,
                    },
                    {
                        path: ProjectPath.Pss.substring(1),
                        element: <PSSProjectClassificationPage />,
                    },
                    {
                        path: ProjectPath.Tra.substring(1),
                        element: <ThreatAndRiskAnalysisListPage />,
                    },
                    {
                        path: `${ProjectPath.Tra.substring(1)}/:id`,
                        element: <ThreatAndRiskAnalysisPage />,
                    },
                    {
                        path: ProjectPath.Vulnerability.substring(1),
                        element: (
                            <Navigate replace to="security-notifications" />
                        ),
                    },
                    {
                        path: ProjectPath.SecurityNotifications.substring(1),
                        index: true,
                        element: <ProjectSecurityNotificationsPage />,
                    },
                    {
                        path: `${ProjectPath.SecurityNotifications.substring(1)}/vulnerabilities`,
                        element: <ProjectVulnerabilitiesPage />,
                    },
                    {
                        path: ProjectPath.Measures.substring(1),
                        element: <MeasuresPage />,
                    },
                    {
                        path: ProjectPath.SystemsAndAssets.substring(1),
                        element: <ProjectSystemsAndAssetsPage />,
                    },
                    {
                        path: `${ProjectPath.SystemsAndAssets.substring(1)}/:sid`,
                        element: <ProjectSystemPage />,
                    },
                    {
                        path: ProjectPath.Assets.substring(1),
                        element: <SecurityZonesAssets />,
                    },
                    {
                        path: `${ProjectPath.Assets.substring(1)}/:zid`,
                        element: <ZoneDetailPage />,
                    },
                    {
                        path: `${ProjectPath.Assets.substring(1)}/requirements`,
                        element: <RequirementListPage />,
                    },
                    {
                        path: `${ProjectPath.Assets.substring(1)}/requirements/:pzid`,
                        element: <RequirementListPage />,
                    },
                    {
                        path: `${ProjectPath.Assets.substring(1)}/requirements/:pzid/:spid`,
                        element: <RequirementListPage />,
                    },
                    {
                        path: ProjectPath.Components.substring(1),
                        element: <ProjectComponentsPage />,
                    },
                    {
                        path: ProjectPath.Cra.substring(1),
                        element: <CraLayout />,
                        children: [
                            {
                                index: true,
                                element: <CraPage />,
                            },
                            {
                                path: ':topic',
                                element: <CraTopicPage />,
                            },
                        ],
                    },
                ],
            },

            {
                path: NavigationPath.Standards,
                element: <StandardsListPage />,
            },
            {
                path: `${NavigationPath.Standards}/:sid`,
                element: <StandardDetailsPage />,
            },
            {
                path: `${NavigationPath.Standards}/:sid/requirements`,
                element: <RequirementListPage />,
            },
            {
                path: `${NavigationPath.Standards}/:sid/:spid/requirements`,
                element: <RequirementListPage />,
            },
            {
                path: NavigationPath.NotificationComponents,
                element: <NotificationComponentsPage />,
            },
            {
                path: NavigationPath.SecurityNotifications,
                element: <GlobalSecurityNotificationsPage />,
            },
            {
                path: NavigationPath.Admin,
                element: <AdminPage />,
                loader: adminLoader,
                children: [
                    {
                        index: true,
                        element: <AdminDashboardPage />,
                    },
                    {
                        path: NavigationPath.AdminUsers.substring(
                            NavigationPath.Admin.length + 1,
                        ),
                        element: <AdminUsersPage />,
                    },
                    {
                        path: NavigationPath.AdminStandards.substring(
                            NavigationPath.Admin.length + 1,
                        ),
                        element: <AdminStandardsPage />,
                    },
                    {
                        path: NavigationPath.AdminRequirements.substring(
                            NavigationPath.Admin.length + 1,
                        ),
                        element: <AdminRequirementsPage />,
                    },
                    {
                        path: NavigationPath.AdminMappings.substring(
                            NavigationPath.Admin.length + 1,
                        ),
                        element: <AdminMappingsPage />,
                    },
                    {
                        path: NavigationPath.AdminPss.substring(
                            NavigationPath.Admin.length + 1,
                        ),
                        element: <AdminPSSIndex />,
                    },
                    {
                        path: NavigationPath.AdminSupplierAssessments.substring(
                            NavigationPath.Admin.length + 1,
                        ),
                        element: <SupplierAssessments />,
                    },
                    {
                        path: NavigationPath.AdminProjectGuidance.substring(
                            NavigationPath.Admin.length + 1,
                        ),
                        element: <AdminProjectGuidancePage />,
                    },
                ],
            },
            {
                path: '/me',
                element: <ProfilePage />,
            },
        ],
    },
];

const router = sentryCreateBrowserRouter(Routes, {
    basename: import.meta.env.BASE_URL,
});

export default router;
